import { TARGET_SCOPES } from './targetDialogModel';

export const TARGET_CYCLE_STATUS = Object.freeze({
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
});

const normalizedStatus = status => (
  ['ACTIVE', 'WORKING'].includes(String(status || '').toUpperCase())
    ? TARGET_CYCLE_STATUS.ACTIVE
    : TARGET_CYCLE_STATUS.ARCHIVED
);

const cycleTargets = (cycle, activeTargets) => {
  if (cycle.targets && typeof cycle.targets === 'object') return cycle.targets;
  return normalizedStatus(cycle.status) === TARGET_CYCLE_STATUS.ACTIVE ? activeTargets : null;
};

/** Normalize target-cycle metadata while keeping the current target map backward compatible. */
export function normalizeTargetCycles(cycles = [], activeTargets = {}) {
  const normalizedCycles = cycles.map((cycle, index) => ({
    ...cycle,
    id: cycle.id || `target-cycle-${index + 1}`,
    baseYear: Number(cycle.baseYear ?? cycle.baselineYear),
    targetYear: Number(cycle.targetYear),
    status: normalizedStatus(cycle.status),
    targets: cycleTargets(cycle, activeTargets),
  })).filter(cycle => Number.isInteger(cycle.baseYear) && Number.isInteger(cycle.targetYear));
  if (normalizedCycles.length > 0) return normalizedCycles;

  const targetList = Object.values(activeTargets || {});
  const baseYear = Math.max(...targetList.map(target => Number(target.baselineYear)).filter(Number.isFinite));
  const targetYear = Math.max(...targetList.map(target => Number(target.targetYear)).filter(Number.isFinite));
  if (!Number.isFinite(baseYear) || !Number.isFinite(targetYear)) return [];
  return [{
    id: `target-cycle-${baseYear}-${targetYear}`,
    baseYear,
    targetYear,
    status: TARGET_CYCLE_STATUS.ACTIVE,
    targets: activeTargets,
  }];
}

/** Resolve the cycle covering an FY; an ending cycle owns its shared Target/Base year boundary. */
export function resolveTargetCycleForYear(cycles = [], fiscalYear) {
  const selectedYear = Number(fiscalYear);
  if (!Number.isInteger(selectedYear)) return null;
  return [...cycles]
    .filter(cycle => selectedYear >= Number(cycle.baseYear) && selectedYear <= Number(cycle.targetYear))
    .sort((left, right) => (
      Number(left.baseYear) - Number(right.baseYear)
      || (left.status === TARGET_CYCLE_STATUS.ARCHIVED ? -1 : 1)
    ))[0] || null;
}

/** Choose a cycle for the dialog, falling back to the current Active cycle outside any cycle. */
export function defaultTargetCycle(cycles = [], fiscalYear) {
  return resolveTargetCycleForYear(cycles, fiscalYear)
    || cycles.find(cycle => cycle.status === TARGET_CYCLE_STATUS.ACTIVE)
    || cycles[0]
    || null;
}

export function targetCycleIsReadOnly(cycle) {
  return cycle?.status === TARGET_CYCLE_STATUS.ARCHIVED;
}

/**
 * Keep the editable Active-cycle boundary aligned with the shared target draft.
 * The draft is the dialog's single source of truth until Save persists both values.
 */
export function synchronizeActiveTargetCyclePeriod(cycles = [], drafts = {}) {
  const activeDraft = drafts?.scope1;
  const baseYear = Number(activeDraft?.baselineYear);
  const targetYear = Number(activeDraft?.targetYear);
  if (!Number.isInteger(baseYear) || !Number.isInteger(targetYear) || targetYear <= baseYear) {
    return cycles;
  }
  return cycles.map(cycle => (
    cycle.status === TARGET_CYCLE_STATUS.ACTIVE
      ? { ...cycle, baseYear, targetYear }
      : cycle
  ));
}

export function targetYearInventoryForCycle(cycle, inventories = []) {
  if (!cycle) return null;
  const inventory = inventories.find(candidate => Number(candidate.year) === Number(cycle.targetYear));
  return inventory && TARGET_SCOPES.every(scope => Number.isFinite(inventory[scope]))
    ? inventory
    : null;
}

export function canCreateNextTargetCycle(cycles = [], inventories = []) {
  const activeCycle = cycles.find(cycle => cycle.status === TARGET_CYCLE_STATUS.ACTIVE);
  return Boolean(activeCycle && targetYearInventoryForCycle(activeCycle, inventories));
}

/** Archive the current cycle and create its successor from verified target-year inventory. */
export function createNextTargetCycle({ cycles = [], inventories = [], targets = {} }) {
  const normalizedCycles = normalizeTargetCycles(cycles, targets);
  const activeCycle = normalizedCycles.find(cycle => cycle.status === TARGET_CYCLE_STATUS.ACTIVE);
  const baseInventory = targetYearInventoryForCycle(activeCycle, inventories);
  if (!activeCycle || !baseInventory) {
    throw new RangeError('Verified target-year inventory is required before creating the next target cycle.');
  }
  const duration = Math.max(Number(activeCycle.targetYear) - Number(activeCycle.baseYear), 1);
  const baseYear = Number(activeCycle.targetYear);
  const targetYear = baseYear + duration;
  const sourceTargets = activeCycle.targets || targets;
  const nextTargets = Object.fromEntries(TARGET_SCOPES.map((scope) => {
    const reductionPercent = Number(sourceTargets?.[scope]?.reductionPercent) || 0;
    return [scope, {
      scope,
      trajectoryType: 'linear',
      baselineYear: baseYear,
      targetYear,
      reductionPercent,
      targetYearEmissions: Number(baseInventory[scope]) * (1 - (reductionPercent / 100)),
    }];
  }));
  return {
    activeCycleId: `target-cycle-${baseYear}-${targetYear}`,
    targets: nextTargets,
    cycles: [
      ...normalizedCycles.map(cycle => (
        cycle.id === activeCycle.id
          ? { ...cycle, status: TARGET_CYCLE_STATUS.ARCHIVED, targets: cycle.targets || targets }
          : cycle
      )),
      {
        id: `target-cycle-${baseYear}-${targetYear}`,
        baseYear,
        targetYear,
        status: TARGET_CYCLE_STATUS.ACTIVE,
        targets: nextTargets,
      },
    ],
  };
}

const actualForScope = (inventory, scope) => {
  if (!inventory) return null;
  if (scope !== 'total') return Number.isFinite(inventory[scope]) ? inventory[scope] : null;
  const values = TARGET_SCOPES.map(scopeId => inventory[scopeId]);
  return values.every(Number.isFinite) ? values.reduce((total, value) => total + value, 0) : null;
};

/** Build a cycle-bounded chart series; inventory years outside the chosen cycle are excluded. */
export function buildTargetCyclePathwaySeries({
  buildTargetPreview, cycle, inventories = [], scope = 'total',
}) {
  if (!cycle || !cycle.targets || typeof buildTargetPreview !== 'function') return [];
  const requestedScopes = scope === 'total' ? TARGET_SCOPES : [scope];
  const previews = Object.fromEntries(requestedScopes.map((scopeId) => {
    const configuredTarget = cycle.targets[scopeId];
    if (!configuredTarget) return [scopeId, null];
    const target = {
      ...configuredTarget,
      scope: scopeId,
      baselineYear: Number(cycle.baseYear),
      targetYear: Number(cycle.targetYear),
    };
    return [scopeId, buildTargetPreview(target, inventories)];
  }));
  const rowsByScope = Object.fromEntries(requestedScopes.map(scopeId => [
    scopeId,
    new Map((previews[scopeId]?.rows || []).map(row => [Number(row.year), row])),
  ]));

  const rows = [];
  for (let year = Number(cycle.baseYear); year <= Number(cycle.targetYear); year += 1) {
    const inventory = inventories.find(candidate => Number(candidate.year) === year);
    const targetValues = requestedScopes.map(scopeId => rowsByScope[scopeId].get(year)?.target);
    const hasTarget = targetValues.every(Number.isFinite);
    rows.push({
      year,
      actual: actualForScope(inventory, scope),
      target: hasTarget
        ? targetValues.reduce((total, value) => total + value, 0)
        : null,
    });
  }
  return rows;
}
